/**
 * Whether the page draws a map indoors (ADR-141 decision D4, ADR-122 decision 6): held at one minus the dim at every
 * hour, with no sky cycle, rain, puddles, wet feet or wind.
 */

/**
 * The part of a manifest's map the indoor rule reads: its id, where it is placed on the world grid, and the compile's
 * indoor mark with its dim.
 */
export interface IndoorMap {
  readonly id: string;
  readonly place?: object;
  readonly indoor?: true;
  readonly dim?: number;
}

/**
 * How a map is lit indoors, or null for a map that follows the sky. A version that says its compile wrote the marks
 * (`lightMarks`) is believed: only a marked map is indoors. A version compiled before the marks existed says nothing, so a map with no
 * place in a world that places others is taken as a room (a room is on the grid or off it, never both, ADR-122 decision
 * 1.5) and held at full light; a world with no grid has no rooms. The rule reads data every compiled version holds, so
 * it is safe with a world that is not republished.
 *
 * @param maps The manifest's maps.
 * @param lightMarks True for a version whose compile wrote the marks (`WorldManifest.lightMarks`); the fallback is off
 *   for it, so a room that follows the sky stays under the sky.
 * @param mapId The map the page stands on.
 * @returns `{ dim }` for an indoor map, null otherwise.
 */
export function indoorOfMap(maps: readonly IndoorMap[], lightMarks: boolean, mapId: string): { readonly dim: number } | null {
  const map = maps.find((candidate) => candidate.id === mapId);
  if (map === undefined) {
    return null;
  }
  if (map.indoor === true) {
    return { dim: map.dim ?? 0 };
  }
  const placed = maps.some((candidate) => candidate.place !== undefined);
  return !lightMarks && placed && map.place === undefined ? { dim: 0 } : null;
}
